// i18n: bundled catalogs (shared/i18n/*.json) + layered server overrides.
//
// Effective text = USER override > HOSPITAL override > GLOBAL override >
// bundled catalog > fallback language > English.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '@shared/i18n/en.json';
import lo from '@shared/i18n/lo.json';
import th from '@shared/i18n/th.json';
import zh from '@shared/i18n/zh.json';
import vi from '@shared/i18n/vi.json';
import themes from '@shared/themes/themes.json';
import { api } from '@/api/client';
import type { Locale, Overrides } from '@/api/types';

type Catalog = Record<string, unknown>;

export const BUNDLED: Record<string, Catalog> = { en, lo, th, zh, vi };
export const BUILTIN_LOCALES: Locale[] = [
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', fallbackLocale: 'en', direction: 'ltr', enabled: true, isBuiltin: true, sortOrder: 1 },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', fallbackLocale: 'en', direction: 'ltr', enabled: true, isBuiltin: true, sortOrder: 2 },
  { code: 'en', name: 'English', nativeName: 'English', fallbackLocale: 'en', direction: 'ltr', enabled: true, isBuiltin: true, sortOrder: 3 },
  { code: 'zh', name: 'Chinese', nativeName: '中文', fallbackLocale: 'en', direction: 'ltr', enabled: true, isBuiltin: true, sortOrder: 4 },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', fallbackLocale: 'en', direction: 'ltr', enabled: true, isBuiltin: true, sortOrder: 5 },
];

const LOCALE_KEY = 'hms.locale';
const OVERRIDES_CACHE_KEY = 'hms.i18nOverrides';
let fallbacks: Record<string, string> = {};

// Intl locale tags for formatting dates, numbers and currency.
const INTL_TAG: Record<string, string> = { lo: 'lo-LA', th: 'th-TH', en: 'en-GB', zh: 'zh-CN', vi: 'vi-VN' };
export const intlTag = (code: string) => INTL_TAG[code] ?? code;

/** Flattens a nested catalog into {"a.b.c": "text"}. */
export function flatten(obj: Catalog, prefix = '', out: Record<string, string> = {}): Record<string, string> {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') flatten(v as Catalog, key, out);
    else if (typeof v === 'string') out[key] = v;
  }
  return out;
}

function setPath(target: Catalog, key: string, value: string) {
  const parts = key.split('.');
  let node = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const next = node[parts[i]];
    if (!next || typeof next !== 'object') node[parts[i]] = {};
    node = node[parts[i]] as Catalog;
  }
  node[parts[parts.length - 1]] = value;
}

/** Rebuilds a language bundle as bundled catalog + overrides. */
export function applyOverrides(locale: string, overrides: Record<string, string>) {
  const merged: Catalog = structuredClone(BUNDLED[locale] ?? {});
  for (const [key, value] of Object.entries(overrides)) setPath(merged, key, value);
  i18n.removeResourceBundle(locale, 'translation');
  i18n.addResourceBundle(locale, 'translation', merged, true, true);
  try {
    const cache = JSON.parse(localStorage.getItem(OVERRIDES_CACHE_KEY) ?? '{}');
    cache[locale] = overrides;
    localStorage.setItem(OVERRIDES_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore storage errors */
  }
}

/** Loads overrides for a locale (authenticated → all layers, else global only). */
export async function loadOverrides(locale: string, authenticated: boolean): Promise<Overrides | null> {
  try {
    if (authenticated) {
      const o = await api.get<Overrides>(`/api/v1/i18n/overrides/${locale}`);
      applyOverrides(locale, o.effective);
      return o;
    }
    const globalOnly = await api.publicGet<Record<string, string>>(`/api/v1/i18n/public/${locale}`);
    applyOverrides(locale, globalOnly);
    return { locale, effective: globalOnly };
  } catch {
    return null; // offline: keep cached/bundled text
  }
}

export function setLocaleMeta(locales: Locale[]) {
  fallbacks = Object.fromEntries(locales.map((l) => [l.code, l.fallbackLocale]));
}

export function storedLocale(): string | null {
  try {
    return localStorage.getItem(LOCALE_KEY);
  } catch {
    return null;
  }
}

function detectLocale(): string {
  const stored = storedLocale();
  if (stored) return stored;
  const nav = (navigator.language || 'lo').toLowerCase();
  const short = nav.split('-')[0];
  return BUNDLED[short] ? short : 'lo';
}

function applyDocumentLocale(code: string) {
  document.documentElement.lang = code;
  const fonts = (themes.fonts as Record<string, string[]>)[code] ?? themes.fonts.en;
  document.documentElement.style.setProperty('--font-family', fonts.map((f) => (f.includes(' ') ? `"${f}"` : f)).join(', '));
}

export async function changeLocale(code: string, authenticated: boolean) {
  try {
    localStorage.setItem(LOCALE_KEY, code);
  } catch {
    /* ignore */
  }
  applyDocumentLocale(code);
  await i18n.changeLanguage(code);
  await loadOverrides(code, authenticated);
}

const initial = detectLocale();
let cachedOverrides: Record<string, Record<string, string>> = {};
try {
  cachedOverrides = JSON.parse(localStorage.getItem(OVERRIDES_CACHE_KEY) ?? '{}');
} catch {
  cachedOverrides = {};
}

void i18n.use(initReactI18next).init({
  resources: Object.fromEntries(Object.entries(BUNDLED).map(([k, v]) => [k, { translation: v }])),
  lng: initial,
  fallbackLng: (code: string) => {
    const chain: string[] = [];
    const fb = fallbacks[code];
    if (fb && fb !== code) chain.push(fb);
    if (!chain.includes('en')) chain.push('en');
    return chain;
  },
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { bindI18n: 'languageChanged loaded', bindI18nStore: 'added removed' },
});
for (const [code, o] of Object.entries(cachedOverrides)) applyOverrides(code, o);
applyDocumentLocale(initial);

export default i18n;
